import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { samplesApi } from "@/services/api";
import type { SampleCreate, SampleUpdate } from "@/types";

export function useSampleList(params?: {
  case_id?: number;
  skip?: number;
  limit?: number;
  substrate?: string;
  ignitable_liquid?: string;
  evaporation_level?: number;
}) {
  return useQuery({
    queryKey: ["samples", params],
    queryFn: async () => {
      const response = await samplesApi.list(params);
      return response.data;
    },
  });
}

export function useSampleDetail(id: number | undefined) {
  return useQuery({
    queryKey: ["samples", "detail", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await samplesApi.getDetail(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateSample() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: SampleCreate) => {
      const response = await samplesApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["samples"] });
    },
  });
}

export function useUpdateSample() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SampleUpdate }) => {
      const response = await samplesApi.update(id, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["samples"] });
    },
  });
}

export function useDeleteSample() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await samplesApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["samples"] });
    },
  });
}

export function useUploadSampleFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const response = await samplesApi.upload(id, file);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["samples"] });
    },
  });
}

export function useUploadSampleFiles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ticFile,
      msFile,
    }: {
      id: number;
      ticFile?: File;
      msFile?: File;
    }) => {
      const response = await samplesApi.uploadFiles(id, ticFile, msFile);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["samples"] });
    },
  });
}
